/**
 * Upgrades a product description into the design's long-form layout.
 *
 * Two jobs, both progressive — with this script blocked the description still
 * reads correctly, it just stays flat:
 *
 *   1. The FAQ block at the end of most descriptions is `<p><strong>Q</strong>
 *      <br>A</p>`. Each becomes a <details> accordion.
 *   2. Wide comparison tables get an overflow wrapper so they scroll inside
 *      their column instead of pushing the page sideways.
 */
class TbpsProductDetails extends HTMLElement {
  connectedCallback() {
    try {
      this.#wrapTables();
      this.#buildFaq();
    } catch (error) {
      // A description we can't parse is not worth breaking the page over.
      console.warn('Product details enhancement skipped:', error);
    }
  }

  #wrapTables() {
    this.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('tbps-table-scroll')) return;
      const scroller = document.createElement('div');
      scroller.className = 'tbps-table-scroll tbps-table-scroll--boxed';
      table.classList.add('tbps-table');
      table.replaceWith(scroller);
      scroller.appendChild(table);
    });
  }

  #buildFaq() {
    const wanted = (this.dataset.faqHeading || '').trim().toLowerCase();
    if (!wanted) return;

    const headings = Array.from(this.querySelectorAll('h2, h3'));
    const start = headings.find((h) => h.textContent.trim().toLowerCase() === wanted);
    if (!start) return;

    // Walk forward from the FAQ heading, converting each question paragraph
    // until we hit the next heading or run out of siblings.
    const list = document.createElement('div');
    list.className = 'tbps-faq';
    let node = start.nextElementSibling;
    let index = 0;

    while (node && !/^H[1-3]$/.test(node.tagName)) {
      const next = node.nextElementSibling;

      if (node.tagName === 'P' && node.querySelector('strong')) {
        const strong = node.querySelector('strong');
        const question = strong.textContent.trim();

        // Everything after the <strong> (and its <br>) is the answer.
        const answer = document.createElement('div');
        answer.className = 'tbps-faq__a';
        let part = strong.nextSibling;
        while (part) {
          const after = part.nextSibling;
          if (!(part.nodeName === 'BR' && answer.childNodes.length === 0)) {
            answer.appendChild(part.cloneNode(true));
          }
          part = after;
        }

        const item = document.createElement('details');
        item.className = 'tbps-faq__item';
        if (index === 0 && this.dataset.openFirst === 'true') item.open = true;

        const summary = document.createElement('summary');
        summary.className = 'tbps-faq__q';
        summary.append(question);

        const chevron = document.createElement('span');
        chevron.className = 'tbps-faq__chevron';
        chevron.innerHTML =
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
        summary.appendChild(chevron);

        item.append(summary, answer);
        list.appendChild(item);
        node.remove();
        index += 1;
      }

      node = next;
    }

    if (list.children.length) start.after(list);
  }
}

if (!customElements.get('tbps-product-details')) {
  customElements.define('tbps-product-details', TbpsProductDetails);
}
